/**
 * カラー処理ユーティリティ
 * - 明度計算による白文字／黒文字の自動判定
 * - ホバー時の自動暗化
 * - HEX 形式の検証
 */

/**
 * HEX 文字列 (#FFFFFF または #FFF) を [r, g, b] に変換
 * 不正な値の場合は null を返す
 */
export function hexToRgb(hex: string): [number, number, number] | null {
  if (!hex) return null
  const normalized = hex.startsWith('#') ? hex.slice(1) : hex

  if (normalized.length === 3) {
    const r = parseInt(normalized[0] + normalized[0], 16)
    const g = parseInt(normalized[1] + normalized[1], 16)
    const b = parseInt(normalized[2] + normalized[2], 16)
    if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) return null
    return [r, g, b]
  }

  if (normalized.length === 6) {
    const r = parseInt(normalized.slice(0, 2), 16)
    const g = parseInt(normalized.slice(2, 4), 16)
    const b = parseInt(normalized.slice(4, 6), 16)
    if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) return null
    return [r, g, b]
  }

  return null
}

/**
 * 有効な HEX カラー (#XXX または #XXXXXX) かどうかを判定
 */
export function isValidHex(hex: string): boolean {
  return hexToRgb(hex) !== null
}

/**
 * 背景色から最適な文字色（白 or 黒）を自動判定
 * YIQ 式で明度を計算し、閾値 0.5 で分岐
 */
export function getContrastTextColor(hexBackground: string): '#FFFFFF' | '#000000' {
  const rgb = hexToRgb(hexBackground)
  if (!rgb) return '#FFFFFF'
  const [r, g, b] = rgb
  // ITU-R BT.601 ベースの輝度計算
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
  return luminance > 0.6 ? '#000000' : '#FFFFFF'
}

/**
 * 指定された HEX カラーを暗化（ホバー状態用）
 * amount は 0〜1 の暗化率（デフォルト 0.12 = 12% 暗化）
 */
export function darkenHex(hex: string, amount = 0.12): string {
  const rgb = hexToRgb(hex)
  if (!rgb) return hex
  const factor = 1 - amount
  const [r, g, b] = rgb.map((v) => Math.max(0, Math.min(255, Math.round(v * factor))))
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`.toUpperCase()
}

function relativeLuminance([r, g, b]: [number, number, number]): number {
  const linear = [r, g, b].map((value) => {
    const channel = value / 255
    return channel <= 0.04045 ? channel / 12.92 : Math.pow((channel + 0.055) / 1.055, 2.4)
  })
  return 0.2126 * linear[0] + 0.7152 * linear[1] + 0.0722 * linear[2]
}

function contrastRatio(foreground: [number, number, number], background: [number, number, number]): number {
  const lighter = Math.max(relativeLuminance(foreground), relativeLuminance(background))
  const darker = Math.min(relativeLuminance(foreground), relativeLuminance(background))
  return (lighter + 0.05) / (darker + 0.05)
}

/** 暗色背景で4.5:1を満たすまで、ブランド色を白方向へ最小限だけ補正する。 */
export function accessibleBrandTextColor(color: string, darkBackground = '#030712'): string {
  const foreground = hexToRgb(color)
  const background = hexToRgb(darkBackground)
  if (!foreground || !background) return color
  if (contrastRatio(foreground, background) >= 4.5) return color

  for (let amount = 0.05; amount <= 1; amount += 0.05) {
    const adjusted = foreground.map((channel) => Math.round(channel + (255 - channel) * amount)) as [
      number,
      number,
      number,
    ]
    if (contrastRatio(adjusted, background) >= 4.5) {
      return `#${adjusted.map((channel) => channel.toString(16).padStart(2, '0')).join('')}`.toUpperCase()
    }
  }
  return '#FFFFFF'
}

/**
 * テナントのデフォルトCTAボタン色（不透明HEX）を、CTAボタンの inline style に変換する。
 * 有効な色が渡された場合のみ solid 背景＋自動文字色を返し、CSSクラスの既定グラデーションを上書きする。
 * 無効／未設定なら undefined を返し、既存のグラデーション等をそのまま使わせる。
 */
export function ctaButtonColorStyle(
  color: string | null | undefined,
): { background: string; color: string } | undefined {
  if (typeof color !== 'string' || !isValidHex(color)) return undefined
  return { background: color, color: getContrastTextColor(color) }
}

/**
 * テナントのブランドカラーを、動画タイトルの文字色として使う。
 * 無効／未設定なら undefined を返し、テーマごとの既定アクセント色を維持する。
 */
export function brandTextColorStyle(
  color: string | null | undefined,
  isDark = false,
): { color: string } | undefined {
  if (typeof color !== 'string' || !isValidHex(color)) return undefined
  return { color: isDark ? accessibleBrandTextColor(color) : color }
}

/**
 * テナントのブランドカラーを、グラデ文字の背景に使う（`bg-clip-text` + 透明文字が前提）。
 * ブランドカラー → 12%暗化 の斜めグラデで、既定の青グラデと同じ方向・質感を保つ。
 * 無効／未設定なら undefined を返し、既定のグラデーションをそのまま使わせる。
 */
export function brandGradientTextStyle(
  color: string | null | undefined,
): { backgroundImage: string } | undefined {
  if (typeof color !== 'string' || !isValidHex(color)) return undefined
  return { backgroundImage: `linear-gradient(to bottom right, ${color}, ${darkenHex(color)})` }
}
